import React from "react";
import { getImageUrl } from "utils/imageHelper";
import {getCountryName} from '../../utils/countryData'

export default function ViewExhibitorDetails({ data }) {
  if (!data) return null;

  const resolveImage = (imgPath) => {
    if (!imgPath) return null;
    return typeof imgPath === "string" ? getImageUrl(imgPath) : null;
  };

  const logoUrl = resolveImage(data.logoImage || data.logo_image || data.logo);
  const bannerUrl = resolveImage(
    data.bannerImage || data.banner_image || data.banner
  );

  const isActive =
    data.status?.toLowerCase() === "active" || data.status === true;

  return (
    <div className="flex flex-col gap-6 text-sm text-gray-800">

      {/* =====================================================
          SECTION 1: EXHIBITOR DETAILS
      ====================================================== */}
      <ViewSection
        title="Exhibitor Details"
        subtitle="Main details about the exhibitor."
      >
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">

          <ViewDisplayBox
            label="Title"
            value={data.title}
          />

          <ViewDisplayBox
            label="Exhibitor Category"
            value={
              data.exhibitor_category?.name ||
              data.exhibitor_category?.label ||
              data.category ||
              "N/A"
            }
          />

          {/* Overview */}
          <div className="flex flex-col gap-1 md:col-span-2">
            <span className="view-label">Overview</span>

            <div
              className="view-value"
              dangerouslySetInnerHTML={{
                __html: data.overview || "N/A",
              }}
            />
          </div>
        </div>
      </ViewSection>

      {/* =====================================================
          SECTION 2: CONTACT DETAILS
      ====================================================== */}
      <ViewSection
        title="Contact Details"
        subtitle="Contact and manager information."
      >
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">

          <ViewDisplayBox
            label="Phone Number"
            value={
              data.phone_number
                ? `${data.country_code || ""} ${data.phone_number}`
                : "N/A"
            }
          />

          <ViewDisplayBox
            label="Email"
            value={data.email}
          />

          <ViewDisplayBox
            label="Manager Name"
            value={data.manager_name}
          />

          <ViewDisplayBox
            label="Manager Email"
            value={data.manager_email}
          />

          <ViewDisplayBox
            label="Website"
            value={data.website}
          />
        </div>
      </ViewSection>

      {/* =====================================================
          SECTION 3: LOCATION & STATUS
      ====================================================== */}
      <ViewSection
        title="Location & Status"
        subtitle="Exhibitor location and account status."
      >
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">

          <ViewDisplayBox
            label="Country"
            value={data.country?getCountryName(data.country):""}
          />

          <ViewDisplayBox
            label="Hall No"
            value={data.hallNo}
          />

          {/* Status */}
          <div className="flex flex-col gap-1">
            <span className="view-label">Status</span>

            <div className="view-value flex items-center">
              <span>
                {isActive ? "Active" : "Inactive"}
              </span>
            </div>
          </div>

          {/* Eco Friendly */}
          <div className="flex flex-col gap-1">
            <span className="view-label">Eco Friendly</span>

            <div className="view-value flex items-center">
      
                {data.eco_friendly ? "Yes" : "No"}
             
            </div>
          </div>
        </div>
      </ViewSection>

      {/* =====================================================
          SECTION 4: BRANDING & MEDIA
      ====================================================== */}
      <ViewSection
        title="Branding & Media"
        subtitle="Uploaded exhibitor branding and promotional media."
      >
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">

          {/* Logo */}
          <div className="flex flex-col gap-1">
            <span className="view-label">
              Logo Image (120 × 40)
            </span>

            <ImageContainer
              src={logoUrl}
              alt="Exhibitor Logo"
              placeholder="No Logo Uploaded"
            />
          </div>

          {/* Banner */}
          <div className="flex flex-col gap-1">
            <span className="view-label">
              Banner Image (393 × 219)
            </span>

            <ImageContainer
              src={bannerUrl}
              alt="Exhibitor Banner"
              placeholder="No Banner Uploaded"
              height="h-32"
            />
          </div>
        </div>
      </ViewSection>

      {/* =====================================================
          SECTION 5: SCHEDULE
      ====================================================== */}
      {data.schedules?.length > 0 && (
        <ViewSection
          title="Schedule"
          subtitle="Exhibitor schedule and event timings."
        >
          <div className="space-y-4">
            {data.schedules.map((schedule, index) => (
              <div
                key={schedule._id || index}
                className="rounded-xl border border-gray-200 bg-gray-50/40 p-4"
              >
                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">

                  <ViewDisplayBox
                    label="Day"
                    value={schedule.day}
                  />

                  <ViewDisplayBox
                    label="Start Time"
                    value={schedule.startTime}
                  />

                  <ViewDisplayBox
                    label="End Time"
                    value={schedule.endTime}
                  />

                  {schedule.date && (
                    <ViewDisplayBox
                      label="Date"
                      value={schedule.date?.split("T")[0]}
                    />
                  )}

                  {schedule.title && (
                    <ViewDisplayBox
                      label="Title"
                      value={schedule.title}
                      className="md:col-span-2"
                    />
                  )}

                  {schedule.description && (
                    <div className="flex flex-col gap-1 md:col-span-3">
                      <span className="view-label">Description</span>

                      <div className="view-value">
                        {schedule.description}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </ViewSection>
      )}

      {/* =====================================================
          SECTION 6: ACCOUNT
      ====================================================== */}
      <ViewSection
        title="Account Details"
        subtitle="Exhibitor account information."
      >
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">

          <ViewDisplayBox
            label="Email"
            value={data.email}
          />

          <ViewDisplayBox
            label="Password"
            value={data.password ? "••••••••" : "N/A"}
          />
        </div>
      </ViewSection>
    </div>
  );
}


/* =========================================================
   SECTION COMPONENT
========================================================= */

function ViewSection({ title, subtitle, children }) {
  return (
    <div className="view-card flex flex-col gap-6 text-sm">
      <div>
        <div className="view-subtitle">{title}</div>

        {/* {subtitle && (
          <p className="mt-1 text-xs text-gray-500">
            {subtitle}
          </p>
        )} */}
      </div>

      <div>{children}</div>
    </div>
  );
}


/* =========================================================
   DISPLAY VALUE
========================================================= */

function ViewDisplayBox({
  label,
  value,
  className = "",
}) {
  return (
    <div className={`flex min-w-0 flex-col gap-1 ${className}`}>
      <span className="view-label">
        {label}
      </span>

      <div className="view-value">
        {value || "N/A"}
      </div>
    </div>
  );
}


/* =========================================================
   IMAGE CONTAINER
========================================================= */

function ImageContainer({
  src,
  alt,
  placeholder,
  height = "h-28",
}) {
  return (
    <div className={`${height} view-image-div`}>
      {src ? (
        <img
          src={src}
          alt={alt || "Media preview"}
          className="max-h-full max-w-full object-contain"
        />
      ) : (
        <span className="text-xs text-gray-400">
          {placeholder}
        </span>
      )}
    </div>
  );
}